import { CSRF_COOKIE } from '@fos/shared/contracts/identity';
import {
  ALLOWED_FILE_EXTENSIONS,
  type ClientDocumentCategory,
  FILE_MESSAGES,
  type FileDto,
  type FileOwnerType,
  fileExtensionOf,
  MAX_FILE_SIZE_BYTES,
} from '@fos/shared/contracts/knowledge';
import { type AppErrorCode, isAppErrorCode } from '@fos/shared/errors';
import { ApiError, csrfHeaders, NetworkError, readCookie } from './api';

/**
 * Wgranie pliku (REST `POST /api/files`, multipart): tRPC nie przenosi plików. Ciasteczko sesji + nagłówek CSRF
 * jak każda mutacja. Serwer sprawdza typ po treści (magic bytes), rozmiar, zakres i właściciela; tu tylko szybka
 * podpowiedź przed wysłaniem (rozszerzenie i 25 MB). Wspólne dla modułów (Wiedza, dokumenty klienta — 17.10.2026
 * przeniesione z features/knowledge); komponent wyboru i upuszczania: `ui/FileDropzone`.
 */

export interface UploadFields {
  clientId?: string | null;
  projectId?: string | null;
  ownerType?: FileOwnerType | null;
  ownerId?: string | null;
  /** dokument klienta (`ownerType: 'client'`): rodzaj i krótki opis */
  category?: ClientDocumentCategory | null;
  description?: string | null;
}

/** Komunikat błędu przed wysłaniem albo null (pusty, za duży, niedozwolone rozszerzenie). */
export function checkFileBeforeUpload(file: File): string | null {
  if (file.size === 0) return FILE_MESSAGES.empty;
  if (file.size > MAX_FILE_SIZE_BYTES) return FILE_MESSAGES.tooLarge;
  const ext = fileExtensionOf(file.name);
  if (!ext || !(ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(ext)) return FILE_MESSAGES.notAllowed;
  return null;
}

async function ensureCsrf(): Promise<void> {
  if (readCookie(CSRF_COOKIE)) return;
  try {
    await fetch('/api/auth/session', { credentials: 'same-origin', headers: { accept: 'application/json' } });
  } catch {
    // brak sieci obsłuży właściwe żądanie
  }
}

const CODE_BY_STATUS: Record<number, AppErrorCode> = {
  400: 'VALIDATION_ERROR',
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'VALIDATION_ERROR',
  503: 'INTEGRATION_UNAVAILABLE',
};

type RawFileDto = Omit<FileDto, 'createdAt'> & { createdAt: string };

/** Wgrywa plik; zwraca FileDto albo rzuca ApiError (komunikat po polsku) / NetworkError. */
export async function uploadFile(file: File, fields: UploadFields = {}, signal?: AbortSignal): Promise<FileDto> {
  await ensureCsrf();
  const form = new FormData();
  // pola przed plikiem: serwer czyta strumień po kolei
  for (const [key, value] of Object.entries(fields)) {
    if (typeof value === 'string' && value !== '') form.append(key, value);
  }
  form.append('file', file, file.name);

  let response: Response;
  try {
    response = await fetch('/api/files', {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...csrfHeaders() },
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }

  const text = await response.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!response.ok) {
    const body = (data as { error?: { code?: unknown; message?: unknown; details?: unknown } } | null)?.error;
    if (response.status >= 500 && !body) throw new NetworkError();
    const code: AppErrorCode = isAppErrorCode(body?.code)
      ? body.code
      : (CODE_BY_STATUS[response.status] ?? (response.status >= 500 ? 'INTERNAL' : 'VALIDATION_ERROR'));
    const message =
      typeof body?.message === 'string' ? body.message : response.status === 413 ? FILE_MESSAGES.tooLarge : undefined;
    throw new ApiError({ code, status: response.status, message, details: body?.details });
  }
  const raw = data as RawFileDto;
  return { ...raw, createdAt: new Date(raw.createdAt) };
}
